import { useState, useEffect } from "react";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AdminSidebar, SIDEBAR_WIDTH_KEY, DEFAULT_WIDTH } from "@/components/AdminSidebar";
import { DashboardLayoutSkeleton } from "@/components/DashboardLayoutSkeleton";
import { useAuth } from "@/_core/hooks/useAuth";
import { hasRole } from "@/lib/roles";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Loader2, Save, Palette, Building2, Scale, Mail, Dumbbell, Database, AlertTriangle, Trash2, Info, Lock } from "lucide-react";
import { LogoUploader } from "@/components/LogoUploader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function Settings() {
  const { user, isLoading: authLoading } = useAuth();
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_WIDTH_KEY);
    return saved ? parseInt(saved, 10) : DEFAULT_WIDTH;
  });

  // Check if user is admin
  const isAdmin = user && hasRole(user.roles, "admin");

  // Only render content if authorized
  if (authLoading) {
    return <DashboardLayoutSkeleton />;
  }

  if (!isAdmin) {
    return (
      <SidebarProvider>
        <AdminSidebar setSidebarWidth={setSidebarWidth} />
        <SidebarInset>
          <div className="flex items-center justify-center h-96">
            <p className="text-muted-foreground">Zugriff verweigert. Nur Administratoren können auf diese Seite zugreifen.</p>
          </div>
        </SidebarInset>
      </SidebarProvider>
    );
  }

  return <SettingsContent sidebarWidth={sidebarWidth} setSidebarWidth={setSidebarWidth} />;
}

function SettingsContent({ sidebarWidth, setSidebarWidth }: { sidebarWidth: number; setSidebarWidth: (width: number) => void }) {
  const utils = trpc.useUtils();
  
  // Fetch current config
  const { data: config, isLoading } = trpc.config.get.useQuery();
  
  // Update mutation
  const updateConfig = trpc.config.update.useMutation({
    onSuccess: () => {
      toast.success("Einstellungen gespeichert", {
        description: "Die Konfiguration wurde erfolgreich aktualisiert.",
      });
      utils.config.get.invalidate();
    },
    onError: (error) => {
      toast.error("Fehler", {
        description: error.message || "Die Einstellungen konnten nicht gespeichert werden.",
      });
    },
  });
  
  // Form state
  const [formData, setFormData] = useState<any>(null);
  
  // Initialize form data when config loads
  useEffect(() => {
    if (config && !formData) {
      setFormData(config);
    }
  }, [config, formData]);
  
  const gymEnabled = config?.features?.gymEnabled ?? false;
  
  if (isLoading || !formData) {
    return (
      <SidebarProvider>
        <AdminSidebar setSidebarWidth={setSidebarWidth} />
        <SidebarInset>
          <div className="flex items-center justify-center h-96">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </SidebarInset>
      </SidebarProvider>
    );
  }
  
  const handleSave = () => {
    // Remove fields that shouldn't be updated
    const { tenantId, domain, features, customDomains, ...updates } = formData;
    updateConfig.mutate(updates);
  };
  
  const updateField = (section: string, field: string, value: any) => {
    setFormData((prev: any) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };
  
  const updateNestedField = (section: string, subsection: string, field: string, value: any) => {
    setFormData((prev: any) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [subsection]: {
          ...prev[section][subsection],
          [field]: value,
        },
      },
    }));
  };
  
  return (
    <SidebarProvider>
      <AdminSidebar setSidebarWidth={setSidebarWidth} />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 border-b px-4">
          <SidebarTrigger className="-ml-1" />
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold">Einstellungen</h1>
          </div>
        </header>
        <div className="flex flex-1 flex-col gap-4 p-4">
          <div className="container py-8 max-w-5xl">
      
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-muted-foreground">
            Verwalten Sie die Konfiguration Ihrer Plattform
          </p>
        </div>
        <Button onClick={handleSave} disabled={updateConfig.isPending}>
          {updateConfig.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Speichern...
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              Speichern
            </>
          )}
        </Button>
      </div>
      
      <Tabs defaultValue="branding" className="space-y-6">
        <TabsList className={`grid w-full ${gymEnabled ? 'grid-cols-6' : 'grid-cols-5'}`}>
          <TabsTrigger value="branding" className="flex items-center gap-2">
            <Palette className="h-4 w-4" />
            <span className="hidden sm:inline">Branding</span>
          </TabsTrigger>
          <TabsTrigger value="contact" className="flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            <span className="hidden sm:inline">Kontakt</span>
          </TabsTrigger>
          <TabsTrigger value="legal" className="flex items-center gap-2">
            <Scale className="h-4 w-4" />
            <span className="hidden sm:inline">Rechtliches</span>
          </TabsTrigger>
          <TabsTrigger value="email" className="flex items-center gap-2">
            <Mail className="h-4 w-4" />
            <span className="hidden sm:inline">E-Mail</span>
          </TabsTrigger>
          {gymEnabled && (
            <TabsTrigger value="gym" className="flex items-center gap-2">
              <Dumbbell className="h-4 w-4" />
              <span className="hidden sm:inline">Gym</span>
            </TabsTrigger>
          )}
          <TabsTrigger value="data" className="flex items-center gap-2">
            <Database className="h-4 w-4" />
            <span className="hidden sm:inline">Daten</span>
          </TabsTrigger>
        </TabsList>
        
        {/* Branding Tab */}
        <TabsContent value="branding" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Branding & Design</CardTitle>
              <CardDescription>
                Passen Sie das Erscheinungsbild Ihrer Plattform an
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <LogoUploader
                value={formData.branding?.logo || null}
                onChange={(url) => updateField("branding", "logo", url || "")}
              />
              
              <div className="space-y-2">
                <Label htmlFor="favicon">Favicon URL (optional)</Label>
                <Input
                  id="favicon"
                  value={formData.branding?.favicon || ""}
                  onChange={(e) => updateField("branding", "favicon", e.target.value)}
                  placeholder="https://example.com/favicon.ico"
                />
              </div>
              
              <Separator />
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="primaryColor">Primärfarbe</Label>
                  <div className="flex gap-2">
                    <Input
                      id="primaryColor"
                      type="color"
                      value={formData.branding?.primaryColor || "#7AB51D"}
                      onChange={(e) => updateField("branding", "primaryColor", e.target.value)}
                      className="w-20 h-10"
                    />
                    <Input
                      value={formData.branding?.primaryColor || "#7AB51D"}
                      onChange={(e) => updateField("branding", "primaryColor", e.target.value)}
                      placeholder="#7AB51D"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="secondaryColor">Sekundärfarbe</Label>
                  <div className="flex gap-2">
                    <Input
                      id="secondaryColor"
                      type="color"
                      value={formData.branding?.secondaryColor || "#F7941D"}
                      onChange={(e) => updateField("branding", "secondaryColor", e.target.value)}
                      className="w-20 h-10"
                    />
                    <Input
                      value={formData.branding?.secondaryColor || "#F7941D"}
                      onChange={(e) => updateField("branding", "secondaryColor", e.target.value)}
                      placeholder="#F7941D"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Contact Tab */}
        <TabsContent value="contact" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Kontaktinformationen</CardTitle>
              <CardDescription>
                Verwalten Sie Ihre Kontaktdaten und Öffnungszeiten
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Telefon</Label>
                  <Input
                    id="phone"
                    value={formData.contact?.phone || ""}
                    onChange={(e) => updateField("contact", "phone", e.target.value)}
                    placeholder="+49 123 456789"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="contactEmail">E-Mail</Label>
                  <Input
                    id="contactEmail"
                    type="email"
                    value={formData.contact?.email || ""}
                    onChange={(e) => updateField("contact", "email", e.target.value)}
                    placeholder="info@example.com"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="website">Website (optional)</Label>
                <Input
                  id="website"
                  value={formData.contact?.website || ""}
                  onChange={(e) => updateField("contact", "website", e.target.value)}
                  placeholder="https://example.com"
                />
              </div>
              
              <Separator />
              
              <div className="space-y-2">
                <Label>Adresse</Label>
                <div className="grid grid-cols-1 gap-3 pl-4 border-l-2">
                  <Input
                    placeholder="Straße und Hausnummer"
                    value={formData.contact?.address?.street || ""}
                    onChange={(e) => updateNestedField("contact", "address", "street", e.target.value)}
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      placeholder="PLZ"
                      value={formData.contact?.address?.zip || ""}
                      onChange={(e) => updateNestedField("contact", "address", "zip", e.target.value)}
                    />
                    <Input
                      placeholder="Stadt"
                      value={formData.contact?.address?.city || ""}
                      onChange={(e) => updateNestedField("contact", "address", "city", e.target.value)}
                    />
                  </div>
                  <Input
                    placeholder="Land"
                    value={formData.contact?.address?.country || ""}
                    onChange={(e) => updateNestedField("contact", "address", "country", e.target.value)}
                  />
                </div>
              </div>
              
              <Separator />
              
              <div className="space-y-2">
                <Label htmlFor="aboutText">Über uns (optional)</Label>
                <Textarea
                  id="aboutText"
                  value={formData.contact?.aboutText || ""}
                  onChange={(e) => updateField("contact", "aboutText", e.target.value)}
                  placeholder="Beschreiben Sie Ihre Praxis, Ihr Team und Ihre Philosophie..."
                  rows={6}
                />
                <p className="text-sm text-muted-foreground">
                  Dieser Text wird auf der Startseite angezeigt
                </p>
              </div>
              
              <Separator />
              
              <div className="space-y-2">
                <Label htmlFor="openingHours">Öffnungszeiten (optional)</Label>
                <Textarea
                  id="openingHours"
                  value={formData.contact?.openingHours?.join("\n") || ""}
                  onChange={(e) => updateField("contact", "openingHours", e.target.value.split("\n"))}
                  placeholder="Mo-Fr: 9:00-18:00&#10;Sa: 10:00-14:00"
                  rows={4}
                />
                <p className="text-sm text-muted-foreground">
                  Eine Zeile pro Öffnungszeit
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Legal Tab */}
        <TabsContent value="legal" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Rechtliche Informationen</CardTitle>
              <CardDescription>
                Verwalten Sie Firmendaten und rechtliche Links
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="companyName">Firmenname</Label>
                <Input
                  id="companyName"
                  value={formData.legal?.companyName || ""}
                  onChange={(e) => updateField("legal", "companyName", e.target.value)}
                  placeholder="Meine Firma GmbH"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="taxId">Steuernummer (optional)</Label>
                  <Input
                    id="taxId"
                    value={formData.legal?.taxId || ""}
                    onChange={(e) => updateField("legal", "taxId", e.target.value)}
                    placeholder="DE123456789"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="registrationNumber">Handelsregisternummer (optional)</Label>
                  <Input
                    id="registrationNumber"
                    value={formData.legal?.registrationNumber || ""}
                    onChange={(e) => updateField("legal", "registrationNumber", e.target.value)}
                    placeholder="HRB 12345"
                  />
                </div>
              </div>
              
              <Separator />
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="imprintUrl">Impressum URL (optional)</Label>
                  <Input
                    id="imprintUrl"
                    value={formData.legal?.imprintUrl || ""}
                    onChange={(e) => updateField("legal", "imprintUrl", e.target.value)}
                    placeholder="https://example.com/impressum"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="privacyPolicyUrl">Datenschutz URL (optional)</Label>
                  <Input
                    id="privacyPolicyUrl"
                    value={formData.legal?.privacyPolicyUrl || ""}
                    onChange={(e) => updateField("legal", "privacyPolicyUrl", e.target.value)}
                    placeholder="https://example.com/datenschutz"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="termsUrl">AGB URL (optional)</Label>
                  <Input
                    id="termsUrl"
                    value={formData.legal?.termsUrl || ""}
                    onChange={(e) => updateField("legal", "termsUrl", e.target.value)}
                    placeholder="https://example.com/agb"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Email Tab */}
        <TabsContent value="email" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>E-Mail-Konfiguration</CardTitle>
              <CardDescription>
                Einstellungen für ausgehende E-Mails
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="senderName">Absendername</Label>
                  <Input
                    id="senderName"
                    value={formData.email?.senderName || ""}
                    onChange={(e) => updateField("email", "senderName", e.target.value)}
                    placeholder="Meine Firma"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="senderEmail">Absender-E-Mail</Label>
                  <Input
                    id="senderEmail"
                    type="email"
                    value={formData.email?.senderEmail || ""}
                    onChange={(e) => updateField("email", "senderEmail", e.target.value)}
                    placeholder="noreply@example.com"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="replyToEmail">Antwort-E-Mail (optional)</Label>
                <Input
                  id="replyToEmail"
                  type="email"
                  value={formData.email?.replyToEmail || ""}
                  onChange={(e) => updateField("email", "replyToEmail", e.target.value)}
                  placeholder="info@example.com"
                />
                <p className="text-sm text-muted-foreground">
                  E-Mail-Adresse für Antworten auf automatische E-Mails
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="emailSignature">E-Mail-Signatur (optional)</Label>
                <Textarea
                  id="emailSignature"
                  value={formData.email?.emailSignature || ""}
                  onChange={(e) => updateField("email", "emailSignature", e.target.value)}
                  placeholder="Mit freundlichen Grüßen&#10;Ihr Team"
                  rows={4}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Gym Tab - Only visible if gym feature is enabled */}
        {gymEnabled && formData.gym && (
          <TabsContent value="gym" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Gym-Einstellungen</CardTitle>
                <CardDescription>
                  Konfiguration für das Gym-Buchungssystem
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="slotDuration">Slot-Dauer (Minuten)</Label>
                    <Input
                      id="slotDuration"
                      type="number"
                      min="15"
                      value={formData.gym?.slotDurationMinutes || 60}
                      onChange={(e) => updateField("gym", "slotDurationMinutes", parseInt(e.target.value))}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="maxParticipants">Max. Teilnehmer pro Slot</Label>
                    <Input
                      id="maxParticipants"
                      type="number"
                      min="1"
                      value={formData.gym?.maxParticipantsPerSlot || 10}
                      onChange={(e) => updateField("gym", "maxParticipantsPerSlot", parseInt(e.target.value))}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="cancellationDeadline">Stornierungsfrist (Stunden)</Label>
                    <Input
                      id="cancellationDeadline"
                      type="number"
                      min="0"
                      value={formData.gym?.cancellationDeadlineHours || 24}
                      onChange={(e) => updateField("gym", "cancellationDeadlineHours", parseInt(e.target.value))}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="maxBookingsPerWeek">Max. Buchungen pro Woche</Label>
                    <Input
                      id="maxBookingsPerWeek"
                      type="number"
                      min="1"
                      value={formData.gym?.maxBookingsPerWeek || 5}
                      onChange={(e) => updateField("gym", "maxBookingsPerWeek", parseInt(e.target.value))}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="bookingPreviewDays">Buchungsvorschau (Tage)</Label>
                    <Input
                      id="bookingPreviewDays"
                      type="number"
                      min="1"
                      value={formData.gym?.bookingPreviewDays || 14}
                      onChange={(e) => updateField("gym", "bookingPreviewDays", parseInt(e.target.value))}
                    />
                  </div>
                </div>
                
                <Separator />
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Erinnerungs-E-Mails senden</Label>
                      <p className="text-sm text-muted-foreground">
                        Automatische E-Mails vor gebuchten Slots
                      </p>
                    </div>
                    <Switch
                      checked={formData.gym?.sendReminderEmails ?? true}
                      onCheckedChange={(checked) => updateField("gym", "sendReminderEmails", checked)}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Admin-Benachrichtigungen</Label>
                      <p className="text-sm text-muted-foreground">
                        Benachrichtigungen bei neuen Buchungen
                      </p>
                    </div>
                    <Switch
                      checked={formData.gym?.sendAdminNotifications ?? true}
                      onCheckedChange={(checked) => updateField("gym", "sendAdminNotifications", checked)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
        
        {/* Data Management Tab */}
        <TabsContent value="data" className="space-y-6">
          <SeedDataManagement />
        </TabsContent>
      </Tabs>
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}

/**
 * Component for managing seed/demo data
 */
function SeedDataManagement() {
  const utils = trpc.useUtils();
  const { data: seedInfo, isLoading } = trpc.seedData.check.useQuery();
  const deleteSeedData = trpc.seedData.delete.useMutation({
    onSuccess: (result) => {
      const totalDeleted = Object.values(result.deleted).reduce((a, b) => a + b, 0);
      toast.success("Demo-Daten gelöscht", {
        description: `${totalDeleted.toLocaleString("de-DE")} Datensätze wurden erfolgreich entfernt.`,
      });
      utils.seedData.check.invalidate();
    },
    onError: (error) => {
      toast.error("Fehler", {
        description: error.message || "Die Demo-Daten konnten nicht gelöscht werden.",
      });
    },
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const hasSeedData = seedInfo?.hasSeedData ?? false;
  const counts = seedInfo?.counts ?? {
    instructors: 0,
    courses: 0,
    bookings: 0,
    sessions: 0,
    attendance: 0,
  };
  const totalRecords = Object.values(counts).reduce((a, b) => a + b, 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="h-5 w-5" />
          Datenverwaltung
        </CardTitle>
        <CardDescription>
          Verwalten Sie Demo-Daten und Beispieldatensätze in Ihrer Datenbank
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {hasSeedData ? (
          <>
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="space-y-2">
                  <p className="font-medium text-amber-800">
                    Demo-Daten erkannt
                  </p>
                  <p className="text-sm text-amber-700">
                    Diese Instanz enthält {totalRecords.toLocaleString("de-DE")} Beispieldatensätze, 
                    die für Demonstrations- und Testzwecke erstellt wurden.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
              <div className="rounded-lg border p-3 text-center">
                <p className="text-2xl font-bold">{counts.instructors}</p>
                <p className="text-xs text-muted-foreground">Kursleiter</p>
              </div>
              <div className="rounded-lg border p-3 text-center">
                <p className="text-2xl font-bold">{counts.courses}</p>
                <p className="text-xs text-muted-foreground">Kurse</p>
              </div>
              <div className="rounded-lg border p-3 text-center">
                <p className="text-2xl font-bold">{counts.bookings}</p>
                <p className="text-xs text-muted-foreground">Buchungen</p>
              </div>
              <div className="rounded-lg border p-3 text-center">
                <p className="text-2xl font-bold">{counts.sessions}</p>
                <p className="text-xs text-muted-foreground">Termine</p>
              </div>
              <div className="rounded-lg border p-3 text-center">
                <p className="text-2xl font-bold">{counts.attendance}</p>
                <p className="text-xs text-muted-foreground">Anwesenheit</p>
              </div>
            </div>

            <Separator />

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <p className="font-medium">Demo-Daten löschen</p>
                <p className="text-sm text-muted-foreground">
                  Entfernt alle automatisch generierten Beispieldaten. 
                  Manuell erstellte Daten bleiben erhalten.
                </p>
              </div>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" className="flex-shrink-0">
                    <Trash2 className="mr-2 h-4 w-4" />
                    Demo-Daten löschen
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Demo-Daten löschen?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Diese Aktion löscht alle {totalRecords.toLocaleString("de-DE")} Demo-Datensätze 
                      unwiderruflich. Manuell erstellte Daten (Kurse, Buchungen, etc.) bleiben erhalten.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => deleteSeedData.mutate()}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      {deleteSeedData.isPending ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Löschen...
                        </>
                      ) : (
                        "Ja, löschen"
                      )}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </>
        ) : (
          <div className="text-center py-8">
            <Database className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="font-medium">Keine Demo-Daten vorhanden</p>
            <p className="text-sm text-muted-foreground mt-1">
              Alle Daten in dieser Instanz wurden manuell erstellt.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
